import { usePage, router } from '@inertiajs/react'

/**
 * Parsed form of the `selected` query param that drives which table row's detail
 * sheet is open. The param is the single source of truth shared by the active-row
 * highlight, the open sheet, and (via U3) the keyboard cursor — so refresh
 * re-opens the sheet, browser Back closes it, and the open row is deep-linkable.
 *
 * Polymorphic rows (donors: individual vs organization) encode their type:
 * `selected=individual:123`. Plain rows use the bare id: `selected=123`.
 */
export interface SelectedRow {
  /** The id portion of the param, always a string. */
  id: string
  /** The optional type prefix; null for non-polymorphic rows. */
  type: string | null
  /** The raw param value (`"123"` or `"individual:123"`). Matches `getRowId`. */
  raw: string
}

export function parseSelectedParam(value: string | null): SelectedRow | null {
  if (!value) return null
  const idx = value.indexOf(':')
  if (idx === -1) return { id: value, type: null, raw: value }
  return { type: value.slice(0, idx), id: value.slice(idx + 1), raw: value }
}

export interface UseSelectedRowResult {
  selected: SelectedRow | null
  /** Open the sheet for a row, writing `selected` to the URL. */
  select: (id: string | number, type?: string | null) => void
  /** Close the sheet, removing `selected` from the URL. */
  clear: () => void
}

/**
 * Reads the `selected` row from the current URL and exposes helpers to change it.
 * Selection mutates only the `selected` param against the *live* URL, preserving
 * every other param already there (filters, q[s], page, view) — so opening a row
 * never drops the active sort/filter/page.
 *
 * The visit is a minimal **partial reload** (`only: ['pagy']`): it stays on
 * Inertia's router so history.state remains valid (deep-link/refresh/Back work),
 * but returns a tiny payload instead of re-rendering the whole index. Note this
 * is a small partial request, not literally no-fetch — a true client-side URL
 * update via raw `window.history` corrupts Inertia 2's history (its popstate
 * handler requires `state.page`), so the minimal partial is the safe tradeoff.
 */
export function useSelectedRow(basePath: string): UseSelectedRowResult {
  const page = usePage()
  const queryIndex = page.url.indexOf('?')
  const query = queryIndex === -1 ? '' : page.url.slice(queryIndex + 1)
  const selected = parseSelectedParam(new URLSearchParams(query).get('selected'))

  function navigate(value: string | null) {
    const params = new URLSearchParams(query)
    if (value) params.set('selected', value)
    else params.delete('selected')
    const qs = params.toString()
    router.get(`${basePath}${qs ? `?${qs}` : ''}`, {}, { only: ['pagy'], preserveState: true, preserveScroll: true })
  }

  return {
    selected,
    select: (id, type) => navigate(type ? `${type}:${id}` : String(id)),
    clear: () => navigate(null),
  }
}
